import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import authKey from "../Account/authKey";
import links from "../Account/links";
import AssessmentDetails from "../components/AssessmentDetails";
import useText from "../hooks/useText";
import cleanPhone from "../utilities/cleanPhone";
import { cleanEmail, validateEmail } from "../utilities/validateEmail";
import getItemDetails from "./getItemDetails";

const Checkout = ({ onBack }: { onBack: VoidFunction }) => {
  const text = useText()?.checkout;
  const elements = useElements();
  const stripe = useStripe();
  const { sku } = useParams<{ sku: string }>();
  let selectedItem: ReturnType<typeof getItemDetails>;
  const history = useHistory();
  try {
    selectedItem = getItemDetails(sku);
  } catch (error) {
    history.goBack();
    return null;
  }

  const [userInfo, setUserInfo] = useState({
    fname: "",
    lname: "",
    email: "",
    password: "",
    passwordConfirm: "",
    phoneNumber: "",
    company: "",
    title: "",
    AuthCodeId: false,
  });

  const [errorInfo, setErrorInfo] = useState({
    fname: "",
    lname: "",
    email: "",
    password: "",
    passwordConfirm: "",
    phoneNumber: "",
    company: "",
    title: "",
  });

  const [cardComplete, setCardComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const auth = useMemo(() => {
    const i = localStorage.getItem(authKey);
    if (!i) return null;
    try {
      return JSON.parse(i).token;
    } catch (e) {
      return null;
    }
  }, []);

  const validate = (field: keyof typeof userInfo | "all") => {
    let valid;
    switch (field) {
      case "email":
        valid = validateEmail(cleanEmail(userInfo.email));
        if (!valid) setErrorInfo({ ...errorInfo, email: "Invalid Email" });
        return valid;
      case "password": {
        const errors = {} as typeof errorInfo;
        if (
          userInfo.passwordConfirm &&
          userInfo.passwordConfirm !== userInfo.password
        )
          errors.passwordConfirm = "Passwords must match";
        if (userInfo.password.length < 6)
          errors.password = "Password must contain 6 characters";
        setErrorInfo({ ...errorInfo, ...errors });
        return Object.values(errors).every((e) => !e);
      }
      case "phoneNumber":
        valid = (cleanPhone(userInfo.phoneNumber) || "").length >= 12;
        if (!valid)
          setErrorInfo({ ...errorInfo, phoneNumber: "Phone number invalid" });
        return valid;
      case "all": {
        if (!cardComplete) return false;
        if (auth) return true;
        const errors = {} as typeof errorInfo;
        const newData = {
          ...userInfo,
          email: cleanEmail(userInfo.email).trim().toLowerCase(),
          phoneNumber: cleanPhone(userInfo.phoneNumber),
        };
        if (!validateEmail(newData.email)) errors.email = "Invalid Email";
        if (userInfo.passwordConfirm !== userInfo.password)
          errors.passwordConfirm = "Passwords must match";
        if (userInfo.password.length < 6)
          errors.password = "Password must contain 6 characters";
        if ((newData.phoneNumber || "").length < 12)
          errors.phoneNumber = "Phone number invalid";
        setErrorInfo({ ...errorInfo, ...errors });
        if (Object.values(errors).every((e) => !e)) return newData;
        return false;
      }
      default:
        valid = userInfo[field];
        if (!valid) setErrorInfo({ ...errorInfo, [field]: "Required" });
        return valid;
    }
  };

  const canSubmit = useMemo(
    () => validate("all"),
    [...Object.values(userInfo), cardComplete]
  );

  const onPlaceOrder = async () => {
    // 0. Guards
    if (!elements || !stripe) return;
    const cleanedInfo = canSubmit; // a clean version of userInfo
    if (!cleanedInfo) return;
    const returnError = (err: any) => {
      setLoading(false);
      return alert(err);
    };
    setLoading(true);
    // 1. Create account if needed
    let authHeader = auth;
    if (!auth) {
      const userCreationResponse = await fetch("/api/user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cleanedInfo),
      }).then((r) => r.json());
      authHeader = userCreationResponse.token;
      if (userCreationResponse.error)
        return returnError(userCreationResponse.error);
      localStorage.setItem(
        authKey,
        JSON.stringify({
          token: authHeader,
          accounts: [],
          teamAdminAccounts: [],
        })
      );
    }
    // 2. Payment
    const payRequest = (data: any) =>
      fetch("/api/store/stripe/pay", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authHeader,
        },
        body: JSON.stringify(data),
      }).then((r) => r.json());
    const stripeResponse = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement)!,
    });
    if (stripeResponse.error) return returnError(stripeResponse.error);
    const paymentResponse = await payRequest({
      // eslint-disable-next-line no-extra-parens
      ...(selectedItem.sku === "MAF"
        ? {
            fname: userInfo.fname,
            lname: userInfo.lname,
          }
        : {}),
      sku: selectedItem.sku,
      paymentMethodId: stripeResponse.paymentMethod.id,
    });
    if (paymentResponse.error) return returnError(paymentResponse.error);
    if (paymentResponse.requiresAuthentication) {
      const authReq = await stripe.handleCardAction(paymentResponse.clientId);
      if (authReq.error)
        return returnError(
          "Your card was not authenticated, please try again."
        );
      if (authReq.paymentIntent.status === "requires_confirmation") {
        const authRes = await payRequest({
          paymentIntentId: authReq.paymentIntent.id,
        });
        if (authRes.error) return returnError("error");
      }
    }
    // 3. Resolve
    setLoading(false);
    switch (selectedItem.sku) {
      case "MA1":
        history.push(links.assessment);
        break;
      case "MAF":
        history.push(links.home);
        break;
      default:
        break;
    }
  };

  const userChangeHandler = (field: keyof typeof userInfo) => (e: any) => {
    setErrorInfo({ ...errorInfo, [field]: "" });
    return setUserInfo({ ...userInfo, [field]: e.target.value });
  };

  return (
    <>
      <Button onClick={onBack} color="primary">
        {text.back}
      </Button>
      <Alert severity="info">
        <AlertTitle>Notice</AlertTitle>
        Use this page if you are buying for yourself. If you are buying for
        another person, please contact{" "}
        <a href="mailto:support@findmojo.com">support@findmojo.com</a>
      </Alert>
      <Grid container justifyContent="center" spacing={2}>
        <Grid xs={6} item>
          <Typography variant="h6">{text.yourOrder}</Typography>
          <Paper
            sx={{
              padding: (t) => t.spacing(2),
              margin: (t) => t.spacing(2, 0),
            }}
          >
            <Grid container spacing={2} wrap="nowrap" alignItems="center">
              <Grid item>
                <img
                  src={selectedItem.image}
                  alt={selectedItem.text.title}
                  style={{
                    display: "block",
                    width: "100%",
                  }}
                />
              </Grid>
              <Grid item>
                <Typography variant="body1" noWrap>
                  <strong>{selectedItem.text.title}</strong>
                </Typography>
                <Typography variant="body1" color="primary">
                  {selectedItem.price}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <>
            <Typography variant="h6">{text.billingDetails}</Typography>
            <Grid
              container
              spacing={2}
              justifyContent="space-between"
              sx={{
                my: (t) => t.spacing(2),
              }}
            >
              <Grid item>
                <Typography variant="body1">{text.total}</Typography>
              </Grid>
              <Grid item>
                <Typography variant="body1">
                  <strong>{selectedItem.price}</strong>
                </Typography>
              </Grid>
            </Grid>
          </>

          <>
            <Grid
              container
              spacing={2}
              sx={{
                my: (t) => t.spacing(2),
              }}
            >
              {(!auth || selectedItem.sku === "MAF") && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      label={text.firstName}
                      value={userInfo.fname}
                      error={Boolean(errorInfo.fname)}
                      helperText={errorInfo.fname || ""}
                      onChange={userChangeHandler("fname")}
                      onBlur={() => validate("fname")}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={text.lastName}
                      value={userInfo.lname}
                      error={Boolean(errorInfo.lname)}
                      helperText={errorInfo.lname || ""}
                      onChange={userChangeHandler("lname")}
                      onBlur={() => validate("lname")}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              {!auth && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label={text.email}
                      value={userInfo.email}
                      error={Boolean(errorInfo.email)}
                      helperText={errorInfo.email || ""}
                      onChange={userChangeHandler("email")}
                      onBlur={() => validate("email")}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      type="password"
                      label={text.password}
                      value={userInfo.password}
                      error={Boolean(errorInfo.password)}
                      helperText={
                        errorInfo.password ||
                        "Password must contain 6 characters"
                      }
                      onBlur={() => validate("password")}
                      onChange={userChangeHandler("password")}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      type="password"
                      label={text.passwordConfirm}
                      value={userInfo.passwordConfirm}
                      error={Boolean(errorInfo.passwordConfirm)}
                      helperText={errorInfo.passwordConfirm || ""}
                      onBlur={() => validate("password")}
                      onChange={userChangeHandler("passwordConfirm")}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={text.company}
                      value={userInfo.company}
                      error={Boolean(errorInfo.company)}
                      helperText={errorInfo.company || ""}
                      onChange={userChangeHandler("company")}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={text.phoneNumber}
                      value={userInfo.phoneNumber}
                      error={Boolean(errorInfo.phoneNumber)}
                      helperText={errorInfo.phoneNumber || ""}
                      onChange={userChangeHandler("phoneNumber")}
                      onBlur={() => validate("phoneNumber")}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={text.jobTitle}
                      value={userInfo.title}
                      error={Boolean(errorInfo.title)}
                      helperText={errorInfo.title || ""}
                      onChange={userChangeHandler("title")}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Box
                  sx={{
                    padding: (t) => t.spacing(0, 2, 2, 2),
                    border: "solid 1px rgba(0,0,0,0.27)",
                    borderRadius: 4,
                  }}
                >
                  <Typography
                    variant="body1"
                    color="primary"
                    sx={{
                      my: (t) => t.spacing(2),
                    }}
                    style={{ fontSize: 12 }}
                  >
                    {text.creditCard} (Stripe)
                  </Typography>
                  <CardElement onChange={(e) => setCardComplete(e.complete)} />
                </Box>
              </Grid>
            </Grid>
          </>
          <Button
            variant="contained"
            color="secondary"
            disabled={loading || !canSubmit}
            onClick={onPlaceOrder}
          >
            {text.placeOrder}
          </Button>
        </Grid>
      </Grid>
      <AssessmentDetails />
    </>
  );
};

export default Checkout;
